import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  Divider,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useEffect } from "react";
import client from "../../utils/client";

const NavLink = ({ to, children, onClick }) => (
  <Link to={to} onClick={onClick}>
    <Button
      fontSize="0.95rem"
      fontWeight={600}
      colorScheme="green"
      variant="link"
      _hover={{ color: "green.800" }}
    >
      {children}
    </Button>
  </Link>
);

const DrawerNavLink = ({ to, children, onClose, bgColor }) => (
  <Link to={to} onClick={onClose} style={{ width: "100%" }}>
    <Box
      w="100%"
      py={3}
      px={4}
      borderRadius="0.5rem"
      bg={bgColor || "transparent"}
      fontWeight={600}
      color={bgColor ? "green.800" : "gray.700"}
      fontSize="1rem"
      _hover={{ bg: bgColor || "green.50", color: "green.700" }}
      transition="all 0.15s"
    >
      {children}
    </Box>
  </Link>
);

const Layout = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const logVisit = async () => {
      try {
        await client.post("/stats/visits");
      } catch (error) {
        console.error("Error logging visit:", error);
      }
    };
    logVisit();
  }, []);

  const handleExit = () => {
    auth.logout(() => navigate("/"));
    onClose();
  };

  const handleImageError = (e) => {
    e.target.src = process.env.PUBLIC_URL + "/placePhoto2.png";
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-b, green.100, gray.50)"
      pb="4rem"
    >
      {/* Sticky Navbar */}
      <Box
        position="sticky"
        top={0}
        zIndex={50}
        backdropFilter="blur(12px)"
        bg="rgba(255,255,255,0.82)"
        boxShadow="0 2px 20px rgba(0,0,0,0.08)"
        borderBottom="1px solid"
        borderColor="green.100"
      >
        <Flex
          maxW="1400px"
          mx="auto"
          px={[4, 6, 8]}
          h={["3.75rem", "4.5rem"]}
          alignItems="center"
          justifyContent="space-between"
          gap={4}
        >
          {/* Logo */}
          <Flex alignItems="center" gap={2} flexShrink={0}>
            <Image
              h={["3.2rem", "3.8rem"]}
              onClick={() => navigate("/")}
              cursor="pointer"
              src={process.env.PUBLIC_URL + "/logoBig.png"}
              _hover={{ opacity: 0.85 }}
              transition="opacity 0.2s"
            />
            <Image
              display={["none", "none", "none", "none", "none", "block"]}
              src={process.env.PUBLIC_URL + "/name.png"}
              h="2rem"
            />
          </Flex>

          {/* Center title on mobile */}
          <Text
            display={["block", "block", "block", "none"]}
            fontSize="0.78rem"
            textAlign="center"
            fontWeight={700}
            color="green.800"
            lineHeight={1.3}
            flex={1}
          >
            Associação Cultural Assistencial Obreiros do Leste
          </Text>

          {/* Desktop Nav */}
          <Flex
            display={["none", "none", "none", "flex"]}
            alignItems="center"
            gap={[3, 4, 5]}
            flexWrap="nowrap"
          >
            <NavLink to="/">Home</NavLink>
            <NavLink to="/agradecimentos">Colaboradores</NavLink>
            <NavLink to="/news">Notícias</NavLink>
            <NavLink to="/blog">Blog</NavLink>
            <NavLink to="/acaolflix">Acaol-Flix</NavLink>
            <NavLink to="/eventos">Eventos</NavLink>

            {!auth.user?.id && (
              <NavLink to="/register">Registrar</NavLink>
            )}

            {/* Admin dropdown */}
            {auth.user?.id && auth.user?.type === process.env.REACT_APP_ADMINISTRATOR && (
              <Menu>
                <MenuButton
                  as={Button}
                  colorScheme="green"
                  variant="solid"
                  size="sm"
                  rightIcon={<ChevronDownIcon />}
                  zIndex={30}
                >
                  Admin
                </MenuButton>
                <MenuList zIndex={30}>
                  <Link to="/dashboard"><MenuItem>Dashboard</MenuItem></Link>
                  <Link to="/allNews"><MenuItem>Gerenciar Notícias</MenuItem></Link>
                  <Link to="/allUsers"><MenuItem>Gerenciar Usuários</MenuItem></Link>
                  <Link to="/allSponsors"><MenuItem>Gerenciar Patrocinadores</MenuItem></Link>
                  <Link to="/allEvents"><MenuItem>Gerenciar Eventos</MenuItem></Link>
                  <Link to="/allVideos"><MenuItem>Gerenciar Acaol-Flix</MenuItem></Link>
                  <Link to="/allBlog"><MenuItem>Gerenciar Blog</MenuItem></Link>
                </MenuList>
              </Menu>
            )}

            {/* Blog author dropdown */}
            {auth.user?.id && auth.user?.type === process.env.REACT_APP_BLOG && (
              <Menu>
                <MenuButton as={Button} colorScheme="green" size="sm" rightIcon={<ChevronDownIcon />} zIndex={30}>
                  Blog
                </MenuButton>
                <MenuList zIndex={30}>
                  <Link to="/createBlog"><MenuItem>Nova Publicação</MenuItem></Link>
                  <Link to="/allBlog"><MenuItem>Minhas Publicações</MenuItem></Link>
                </MenuList>
              </Menu>
            )}

            {/* News author dropdown */}
            {auth.user?.id && auth.user?.type === process.env.REACT_APP_NEWS && (
              <Menu>
                <MenuButton as={Button} colorScheme="green" size="sm" rightIcon={<ChevronDownIcon />} zIndex={30}>
                  Notícias
                </MenuButton>
                <MenuList zIndex={30}>
                  <Link to="/createNews"><MenuItem>Nova Notícia</MenuItem></Link>
                  <Link to="/allNews"><MenuItem>Minhas Notícias</MenuItem></Link>
                </MenuList>
              </Menu>
            )}

            {/* Cultural commission dropdown */}
            {auth.user?.id && auth.user?.type === process.env.REACT_APP_CULTURAL && (
              <Menu>
                <MenuButton as={Button} colorScheme="green" size="sm" rightIcon={<ChevronDownIcon />} zIndex={30}>
                  Cultural
                </MenuButton>
                <MenuList zIndex={30}>
                  <Link to="/allEvents"><MenuItem>Gerenciar Eventos</MenuItem></Link>
                </MenuList>
              </Menu>
            )}

            {/* Auth button */}
            {auth.user?.id ? (
              <Button onClick={handleExit} size="sm" colorScheme="red" variant="outline">
                Sair
              </Button>
            ) : (
              <Link to="/login">
                <Button size="sm" colorScheme="green">Login</Button>
              </Link>
            )}

            {/* Avatar */}
            {auth?.userPhoto && (
              <Link to="/profile">
                <Box
                  w="42px"
                  h="42px"
                  borderRadius="50%"
                  overflow="hidden"
                  border="2px solid"
                  borderColor="green.300"
                  _hover={{ borderColor: "green.500" }}
                  transition="border-color 0.2s"
                >
                  <Image
                    src={auth.userPhoto}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    onError={handleImageError}
                  />
                </Box>
              </Link>
            )}
          </Flex>

          {/* Mobile hamburger */}
          <IconButton
            display={["flex", "flex", "flex", "none"]}
            as={IconButton}
            aria-label="Menu"
            icon={<HamburgerIcon />}
            variant="outline"
            colorScheme="green"
            bg="white"
            size="sm"
            onClick={onOpen}
            flexShrink={0}
          />
        </Flex>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader borderBottomWidth="1px" borderColor="green.100">
            <Flex alignItems="center" gap={2}>
              <Image h="2.5rem" src={process.env.PUBLIC_URL + "/logoBig.png"} />
              <Text fontWeight={700} color="green.700" fontSize="0.9rem">Menu</Text>
            </Flex>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody py={4}>
            <VStack align="stretch" spacing={1}>
              <DrawerNavLink to="/" onClose={onClose}>Home</DrawerNavLink>
              <DrawerNavLink to="/agradecimentos" onClose={onClose}>Colaboradores</DrawerNavLink>
              <DrawerNavLink to="/news" onClose={onClose}>Notícias</DrawerNavLink>
              <DrawerNavLink to="/blog" onClose={onClose}>Blog</DrawerNavLink>
              <DrawerNavLink to="/acaolflix" onClose={onClose}>Acaol-Flix</DrawerNavLink>
              <DrawerNavLink to="/eventos" onClose={onClose}>Eventos</DrawerNavLink>

              {!auth?.user && (
                <>
                  <Divider my={2} />
                  <DrawerNavLink to="/login" onClose={onClose}>Login</DrawerNavLink>
                  <DrawerNavLink to="/register" onClose={onClose}>Registrar</DrawerNavLink>
                </>
              )}

              {auth?.user && (
                <>
                  <Divider my={2} />
                  <DrawerNavLink to="/profile" onClose={onClose}>Meu Perfil</DrawerNavLink>
                  <Box
                    w="100%"
                    py={3}
                    px={4}
                    borderRadius="0.5rem"
                    fontWeight={600}
                    color="red.600"
                    fontSize="1rem"
                    cursor="pointer"
                    _hover={{ bg: "red.50" }}
                    transition="all 0.15s"
                    onClick={handleExit}
                  >
                    Sair
                  </Box>
                </>
              )}

              {auth.user?.type === process.env.REACT_APP_ADMINISTRATOR && (
                <>
                  <Divider my={2} />
                  <Text px={4} fontSize="0.75rem" fontWeight={700} color="green.600" textTransform="uppercase" letterSpacing="0.08em">
                    Administrador
                  </Text>
                  <DrawerNavLink to="/dashboard" onClose={onClose} bgColor="#d6f0de">Dashboard</DrawerNavLink>
                  <DrawerNavLink to="/allNews" onClose={onClose} bgColor="#d6f0de">Gerenciar Notícias</DrawerNavLink>
                  <DrawerNavLink to="/allUsers" onClose={onClose} bgColor="#d6f0de">Gerenciar Usuários</DrawerNavLink>
                  <DrawerNavLink to="/allSponsors" onClose={onClose} bgColor="#d6f0de">Gerenciar Patrocinadores</DrawerNavLink>
                  <DrawerNavLink to="/allEvents" onClose={onClose} bgColor="#d6f0de">Gerenciar Eventos</DrawerNavLink>
                  <DrawerNavLink to="/allVideos" onClose={onClose} bgColor="#d6f0de">Gerenciar Acaol-Flix</DrawerNavLink>
                  <DrawerNavLink to="/allBlog" onClose={onClose} bgColor="#d6f0de">Gerenciar Blog</DrawerNavLink>
                </>
              )}

              {auth.user?.type === process.env.REACT_APP_BLOG && (
                <>
                  <Divider my={2} />
                  <Text px={4} fontSize="0.75rem" fontWeight={700} color="green.600" textTransform="uppercase" letterSpacing="0.08em">
                    Blog
                  </Text>
                  <DrawerNavLink to="/createBlog" onClose={onClose} bgColor="#d6f0de">Nova Publicação</DrawerNavLink>
                  <DrawerNavLink to="/allBlog" onClose={onClose} bgColor="#d6f0de">Minhas Publicações</DrawerNavLink>
                </>
              )}

              {auth.user?.type === process.env.REACT_APP_NEWS && (
                <>
                  <Divider my={2} />
                  <Text px={4} fontSize="0.75rem" fontWeight={700} color="green.600" textTransform="uppercase" letterSpacing="0.08em">
                    Notícias
                  </Text>
                  <DrawerNavLink to="/createNews" onClose={onClose} bgColor="#d6f0de">Nova Notícia</DrawerNavLink>
                  <DrawerNavLink to="/allNews" onClose={onClose} bgColor="#d6f0de">Minhas Notícias</DrawerNavLink>
                </>
              )}

              {auth.user?.type === process.env.REACT_APP_CULTURAL && (
                <>
                  <Divider my={2} />
                  <Text px={4} fontSize="0.75rem" fontWeight={700} color="green.600" textTransform="uppercase" letterSpacing="0.08em">
                    Comissão Cultural
                  </Text>
                  <DrawerNavLink to="/allEvents" onClose={onClose} bgColor="#d6f0de">Gerenciar Eventos</DrawerNavLink>
                </>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Page content */}
      <Box minH="calc(100vh - 4.5rem - 3rem)">
        {children}
      </Box>

      {/* Footer */}
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        width="100%"
        zIndex={2}
        bg="#0B2027"
        borderTop="2px solid"
        borderColor="green.700"
      >
        <Flex
          maxW="1400px"
          mx="auto"
          h="3rem"
          alignItems="center"
          justifyContent={["center", "space-between"]}
          px={[4, 8]}
          gap={4}
        >
          <Text fontSize="0.82rem" color="gray.400" fontFamily="Afacad" whiteSpace="nowrap">
            © {new Date().getFullYear()} ACAOL — Associação Cultural Assistencial Obreiros do Leste
          </Text>
          <Flex gap={4} display={["none", "flex"]} alignItems="center">
            <Link to="/privacy">
              <Text fontSize="0.78rem" color="gray.500" _hover={{ color: "green.300" }} transition="color 0.2s">
                Privacidade
              </Text>
            </Link>
            <Text fontSize="0.78rem" color="gray.600">|</Text>
            <Text fontSize="0.78rem" color="gray.500">
              contato@acaol.org
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default Layout;
